import React, { useEffect, useId, useRef } from "react";
import { useClaudeSignIn } from "@/shared/api/provider-oauth";
import { PrimaryButton, GhostButton } from "@/shared/components/settings-atoms";
import type { ProviderConnection } from "@/shared/types/models";

export default function ClaudeSignIn({ name, connectionId, canManage, onSaved, onCancel, onBusy }: {
  name: string; connectionId?: string; canManage: boolean;
  onSaved: (connection: ProviderConnection) => void; onCancel: () => void;
  onBusy: (busy: boolean) => void;
}) {
  const oauth = useClaudeSignIn();
  const codeInput = useRef<HTMLInputElement>(null);
  const id = useId();
  const attempt = oauth.start.data;
  const busy = oauth.start.isPending || oauth.finish.isPending || oauth.cancel.isPending;
  const error = oauth.start.error || oauth.finish.error || oauth.cancel.error;
  useEffect(() => { onBusy(busy || !!attempt); return () => onBusy(false); }, [busy, attempt, onBusy]);
  return <form className="space-y-3" onSubmit={async (event) => {
    event.preventDefault();
    if (!canManage || busy) return;
    try {
      if (!attempt) { await oauth.start.mutateAsync({ name: name.trim(), connection_id: connectionId }); return; }
      const code = codeInput.current?.value.trim() ?? "";
      if (!code || oauth.stopped) return;
      if (codeInput.current) codeInput.current.value = "";
      onSaved(await oauth.complete(attempt.id, code));
    } catch { /* The sign-in error is shown below, with an explicit restart action. */ }
  }}>
    <div className="space-y-2 rounded-[12px] p-3 bg-[var(--surface-sunk)] text-[12px] text-[var(--text-secondary)]">
      <p>Sign in on Claude’s website and approve access to your subscription. Return here with the one-time authorization code Claude shows you.</p>
      <p>Definable stores the resulting credentials encrypted and renews your login automatically. Your workspace’s agents share your subscription limits.</p>
      <p>Experimental access through Claude Code’s sign-in flow. The consent screen identifies Claude Code. Provider changes may interrupt this connection.</p>
    </div>
    {attempt && <div className="space-y-3">
      <a href={attempt.authorization_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center h-8 px-3 rounded-full bg-[var(--text-primary)] text-[var(--surface)] font-medium">Open Claude sign-in ↗</a>
      <p className="text-[12px] text-[var(--text-tertiary)]">Complete this step within {Math.round(attempt.expires_in / 60)} minutes. Keep this page open while you sign in.</p>
      <div className="space-y-1.5">
        <label htmlFor={id}>One-time authorization code</label>
        <input id={id} ref={codeInput} required maxLength={4096} type="password" autoComplete="off" spellCheck={false} placeholder="Paste the code from Claude" disabled={busy || oauth.stopped} className="w-full h-8 px-2.5 rounded-[8px] bg-[var(--surface)] ring-1 ring-[var(--hairline-strong)] text-[13px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--hairline-focus)]" />
      </div>
    </div>}
    {error && <p role="alert" className="text-[var(--danger)]">{error.message}</p>}
    {oauth.finish.error && !oauth.stopped && <p className="text-[12px] text-[var(--text-secondary)]">Paste the same authorization code to retry. Your sign-in progress is saved until it expires.</p>}
    <div className="flex justify-end gap-2">
      <GhostButton type="button" disabled={busy} onClick={async () => {
        try { await oauth.reset(); onCancel(); } catch { /* Cancellation failure stays visible. */ }
      }}>Cancel</GhostButton>
      {attempt && <GhostButton type="button" disabled={busy} onClick={async () => {
        if (codeInput.current) codeInput.current.value = "";
        try { await oauth.reset(); } catch { /* Cancellation failure stays visible. */ }
      }}>Start again</GhostButton>}
      <PrimaryButton type="submit" disabled={!canManage || !name.trim() || busy || oauth.stopped}>
        {busy ? "Connecting…" : attempt ? "Connect Claude" : "Continue with Claude"}
      </PrimaryButton>
    </div>
  </form>;
}
